import React, {
  ChangeEventHandler,
  useCallback,
  useContext,
  useState,
} from "react";
import {
  faImage,
  faTrash,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { translate } from "../../../../config/translator";
import { ClavaContext } from "../../../../config/contexts";
import { Image as UserImage } from "../../../../client/api";
import client from "../../../../client";

const FileInput: React.FC<{
  preview: string | IconDefinition;
  onChange: (text: UserImage | undefined) => void;
  setFileUploadError?: (err: string) => void;
  name: string;
  disabled?: boolean;
}> = ({ name, disabled, setFileUploadError, preview, onChange }) => {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState<string>();
  const { l } = useContext(ClavaContext);
  const onValueChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      if (!disabled && e.target.files && e.target.files.length === 1) {
        const img = new Image();
        const file = e.target.files[0];
        const filename = file.name;

        const objectUrl = URL.createObjectURL(file);
        setUploading(objectUrl);
        img.onload = () => {
          client()
            .uploadUserMedia(
              { url: objectUrl, filename, file, as: img.width / img.height },
              (p) => {
                setProgress(Math.round(p * 100));
              }
            )
            .then(
              (data) => {
                if (data) {
                  onChange({
                    id: data.id,
                    url: data.variants[0],
                    aspectRatio: img.width / img.height,
                    uploadDate: "",
                  });
                  setTimeout(() => {
                    setUploading(undefined);
                  }, 1000);
                } else if (setFileUploadError)
                  setFileUploadError(translate("uploadError", l));
              },
              (error) => {
                if (setFileUploadError) setFileUploadError(error);
              }
            );
        };
        img.src = objectUrl;
      }
    },
    [disabled, l, onChange, setFileUploadError]
  );
  const reset = useCallback(() => {
    onChange(undefined);
  }, [onChange]);
  return (
    <div className="relative">
      <label htmlFor={name} className="relative">
        {uploading ? (
          <img className="w-full h-auto" src={uploading} alt="preview" />
        ) : typeof preview === "string" ? (
          preview === "" ? (
            <FontAwesomeIcon icon={faImage} className="w-12 h-12" />
          ) : (
            <img className="w-full h-auto" src={preview} alt="preview" />
          )
        ) : (
          <FontAwesomeIcon icon={preview} className="w-12 h-12" />
        )}
        {!!uploading && progress !== 0 && (
          <div
            className="absolute bg-primary-40 top-0 left-0 bottom-0 flex flex-row items-center justify-center text-white transition-all overflow-hidden"
            style={{ width: `${progress}%` }}
          >
            {progress}%
          </div>
        )}
      </label>
      <div className="relative">
        <input
          tabIndex={0}
          type="file"
          accept="image/*"
          name={name}
          id={name}
          onChange={onValueChange}
          className={`w-full p-2 outline-0 rounded-xl active:bg-input-bg-active active:dark:bg-input-bg-active-dark focus:bg-input-bg-active focus:dark:bg-input-bg-active-dark border-[0.5px] active:dark:border-input-border-active-dark focus:dark:border-input-border-active-dark text-font dark:text-font-dark bg-input-bg dark:bg-input-bg-dark border-input-border dark:border-input-border-dark active:border-input-border-active focus:border-input-border-active placeholder-placeholder dark:placeholder-placeholder-dark ${
            disabled ? "opacity-50" : "opacity-100"
          }`}
        />
        {typeof preview === "string" && preview !== "" && (
          <button
            type="button"
            onClick={reset}
            className="p-1 bottom-1.5 right-3 absolute"
          >
            <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

FileInput.defaultProps = { disabled: false, setFileUploadError: undefined };

export default FileInput;
